import { CollapsibleRowMenuButton } from 'components/collapsible-row-menu';
import t from 'react-translate';
import { config } from '@config/pendo.config.json';
import NvTooltip from 'shared/components/nv-tooltip';
import React, { useCallback, useContext, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { ToggleQuestionTypes } from 'redux/schemas/models/mentoring-program-profile-questions';
import MentoringProgramContext from 'athena/components/mentoring-program/context';
import { getMentoringProgramAliases } from 'redux/selectors/mentoring-programs';

const ToggleQuestionTypeButtonIcons = {
  [ToggleQuestionTypes.GENERAL]: 'diverge',
  [ToggleQuestionTypes.PERSONALIZE]: 'converge',
};

type ToggleQuestionTypeButtonProps = {
  mode?: ToggleQuestionTypes;
  onClick: (type: ToggleQuestionTypes) => void;
};

const ToggleQuestionTypeButton = (props: ToggleQuestionTypeButtonProps) => {
  const {
    mode = ToggleQuestionTypes.GENERAL,
    onClick,
  } = props;

  const { mentoringProgram } = useContext(MentoringProgramContext);
  const aliases = useSelector((state) => getMentoringProgramAliases(state, mentoringProgram?.id ?? 0));

  const handleToggle = useCallback(() => {
    const newMode = mode === ToggleQuestionTypes.GENERAL
      ? ToggleQuestionTypes.PERSONALIZE
      : ToggleQuestionTypes.GENERAL;
    onClick(newMode);
  }, [mode, onClick]);

  const icon = useMemo(() => ToggleQuestionTypeButtonIcons[mode], [mode]);
  const translations = useMemo(() => (
    t.MENTORING_PROGRAMS.SETTINGS.TABS.PROGRAM_PROFILE_SETUP.PROGRAM_LEVEL_FIELDS.SWITCH_QUESTION_TYPE
  ), []);
  const tooltip = mode === ToggleQuestionTypes.GENERAL
    ? translations.GENERAL.TOOLTIP({ MentorsAlias: aliases.MentorsAlias ?? '', MenteesAlias: aliases.MenteesAlias ?? '' })
    : translations.PERSONALIZE.TOOLTIP();
  const dataQa = useMemo(() => (
    config.pendo.athena.mentorshipProgram.settings.programProfileSetup.toggleQuestionTypeButton ?? ''
  ), []);

  return (
    <NvTooltip
      text={tooltip}
      placement='top'
      maxWidth={240}
    >
      <div className='d-flex'>
        <CollapsibleRowMenuButton
          icon={icon}
          onClick={handleToggle}
          data-qa={dataQa}
          aria-label={translations[mode].ARIA_LABEL()}
        />
      </div>
    </NvTooltip>
  );
};

export default React.memo(ToggleQuestionTypeButton);
